// src/prototype/components/InitiativesUploadBand.tsx
// ============================================================
// INITIATIVES UPLOAD BAND
//
// The section header promises "compare, deduplicate, and enrich your current initiatives",
// so the upload is REAL: dropping (or browsing to) a CSV/XLSX genuinely parses the file,
// populates the table from the parsed rows, and DERIVES the Status column — Matched against
// the demo library, Duplicate for a repeated name, else New. See utils/initiativesImport.
//
// Parsing never surfaces an error. Any failure falls back to the canonical five rows with a
// quiet note, because an error state on the drop zone mid-demo is worse than a slightly less
// impressive success.
//
// A successful upload moves the Initiatives component of the personalization vector 0 -> 100.
// That component carries NO weight in the composite formula, so the overall number is
// deliberately unchanged by this beat: an initiatives import binds no measures.
// ============================================================

import { SHOW_COMING_SOON } from '../config/demoFlags';
import React, { useCallback, useRef, useState } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  Upload, 
  FileSpreadsheet,
  Info,
  CheckCircle2,
  RefreshCw,
  AlertTriangle,
  Download,
} from 'lucide-react';

import useDemoUploads from '../hooks/useDemoUploads';
import { DEMO_CLIENT } from '../config/demoClient';

const DEMO_CLIENT_NAME = DEMO_CLIENT.name;
import type { DemoInitiative } from '../hooks/useDemoUploads';
import { parseInitiativesFile, canonicalInitiatives } from '../utils/initiativesImport';
import { markPersonalizationChanged } from '../hooks/useEvaluationRun';

// The preview shown before any upload. Statuses are DERIVED here too — the same code path the
// uploaded file goes through — so the preview can never disagree with a real import.
// NOTE: there is deliberately NO pre-seeded preview. Before an import the section shows only
// the drop zone — a table of initiatives the client never uploaded reads as though the system
// already knows things it cannot know. "View sample format" covers the "what shape?" question.

interface InitiativesUploadBandProps {
  className?: string;
  defaultExpanded?: boolean;
}

export function InitiativesUploadBand({ 
  className = '',
  defaultExpanded = false,
}: InitiativesUploadBandProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { initiatives, setInitiatives } = useDemoUploads();

  const hasUploaded = initiatives !== null;
  const rows: DemoInitiative[] = initiatives ?? [];

  // ~1.8s determinate bar, then the parsed table is revealed. The bar is DETERMINATE (real
  // 0→100 ticks on a fixed schedule) rather than an indeterminate spinner so the beat reads as
  // work completing. Parsing usually finishes far sooner; we hold the reveal until the bar
  // lands so the sequence is identical every run.
  const ingest = useCallback(async (file: File) => {
    setBusy(true);
    setProgress(0);
    setFileName(file.name);
    setNote(null);

    const STEP_MS = 90; // 20 steps x 90ms = 1.8s
    let tick = 0;
    const timer = window.setInterval(() => {
      tick += 1;
      setProgress(Math.min(100, tick * 5));
      if (tick >= 20) window.clearInterval(timer);
    }, STEP_MS);

    const [result] = await Promise.all([
      parseInitiativesFile(file),
      new Promise((resolve) => window.setTimeout(resolve, STEP_MS * 20)),
    ]);
    window.clearInterval(timer);
    setProgress(100);

    // Graceful fallback — never an error state on camera.
    const finalRows = result.ok && result.rows.length > 0 ? result.rows : canonicalInitiatives();
    if (!result.ok) {
      setNote(`Using the standard template (${result.note ?? 'unreadable file'}).`);
    }
    setInitiatives(finalRows);
    markPersonalizationChanged(); // re-arms the Evaluate interstitial
    setBusy(false);
  }, [setInitiatives]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) void ingest(f);
  };

  const handleBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void ingest(f);
  };

  const handleIngestionClick = () => {
    alert('Ingestion functionality coming soon!');
  };

  return (
    <>
      <div className={`rounded-xl overflow-hidden transition-all duration-200 ${
        isExpanded 
          ? 'bg-white border border-[#e5e7eb] shadow-sm' 
          : 'bg-white border border-[#e5e7eb] hover:border-[#18365E]/20'
      } ${className}`}>
        {/* Header - Always Visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full flex items-center justify-between p-4 cursor-pointer transition-all duration-150 ease-out group ${
            isExpanded ? 'border-b border-[#e5e7eb]' : ''
          }`}
        >
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors duration-150 ${
              isExpanded 
                ? 'bg-[#18365E] text-white' 
                : 'bg-[#18365E]/10 text-[#18365E] group-hover:bg-[#18365E]/15'
            }`}>
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#18365E]">
                  Existing Initiatives Upload
                </h3>
                {hasUploaded && (
                  <div className="flex items-center gap-1 text-[10px] font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                    <CheckCircle2 className="w-3 h-3" />
                    Uploaded
                  </div>
                )}
              </div>
              <p className="text-xs text-[#717182] mt-0.5">
                {isExpanded 
                  ? 'Compare, deduplicate, and enrich your current initiatives'
                  : 'Upload your existing analytics initiatives for comparison'
                }
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:block text-[10px] font-medium px-2 py-1 rounded bg-[#18365E]/5 text-[#717182]">
              Optional
            </span>
            {isExpanded ? (
              <ChevronUp className="w-5 h-5 text-[#18365E]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#717182] group-hover:text-[#18365E] transition-colors" />
            )}
          </div>
        </button>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="p-4 bg-[#FAFBFC]">
            {/* Upload Dropzone */}
            <div
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              className={`mb-4 p-4 border-2 border-dashed rounded-xl transition-colors ${
                dragActive
                  ? 'border-[#FF6E4C] bg-[#FFF4F1]'
                  : 'border-[#e5e7eb] bg-white hover:border-[#18365E]/30'
              }`}
            >
              <div className="flex flex-col items-center justify-center py-4">
                <div className="w-12 h-12 rounded-full bg-[#18365E]/5 flex items-center justify-center mb-3">
                  {busy
                    ? <RefreshCw className="w-6 h-6 text-[#18365E] animate-spin" />
                    : <Upload className="w-6 h-6 text-[#18365E]" />}
                </div>
                <p className="text-sm font-medium text-[#18365E] mb-1">
                  {busy ? 'Reading your file…' : 'Drag and drop your file here'}
                </p>
                <p className="text-xs text-[#717182] mb-3">CSV / XLSX supported</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv"
                  onChange={handleBrowse}
                  className="hidden"
                  data-testid="initiatives-file-input"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 text-xs font-medium text-[#18365E] bg-white border border-[#e5e7eb] rounded-lg hover:bg-[#F5F7FA] transition-colors"
                >
                  Browse files
                </button>
                {busy && (
                  <div className="w-full max-w-xs mt-1" data-testid="initiatives-progress">
                    <div className="h-1.5 w-full bg-[#e5e7eb] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#FF6E4C] rounded-full transition-all duration-100 ease-linear"
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-[#717182] mt-1 text-center tabular-nums">{progress}%</p>
                  </div>
                )}
                {fileName && !busy && (
                  <p className="text-[11px] text-[#717182] mt-2">
                    Imported <span className="font-semibold text-[#18365E]">{fileName}</span>
                  </p>
                )}
                {note && (
                  <p className="text-[11px] text-[#717182] mt-1 italic">{note}</p>
                )}
              </div>
            </div>

            {/* Imported table — only after a real import. */}
            {hasUploaded && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileSpreadsheet className="w-4 h-4 text-[#18365E]" />
                  <span className="text-xs font-semibold text-[#18365E]">
                    {hasUploaded ? 'Imported Initiatives' : 'Sample Preview'}
                  </span>
                </div>
                <span className="text-xs text-[#717182]">
                  {rows.length} initiatives detected ·{' '}
                  {rows.filter((r) => r.status === 'Matched').length} matched ·{' '}
                  {rows.filter((r) => r.status === 'Duplicate').length} duplicate ·{' '}
                  {rows.filter((r) => r.committed).length} committed
                </span>
              </div>

              <div className="overflow-x-auto rounded-lg border border-[#e5e7eb]">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-[#F5F7FA]">
                      <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                        Initiative Name
                      </th>
                      <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                        Category
                      </th>
                      <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb] hidden md:table-cell">
                        Source
                      </th>
                      <th className="text-right px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                        % Complete
                      </th>
                      <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                        Committed
                      </th>
                      <th className="text-left px-3 py-2 font-semibold text-[#18365E] border-b border-[#e5e7eb]">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((initiative, index) => (
                      <tr 
                        key={index} 
                        className={`${index % 2 === 0 ? 'bg-white' : 'bg-[#FAFBFC]'}`}
                      >
                        <td className="px-3 py-2 text-[#18365E] font-medium">
                          {initiative.name}
                          {(initiative.warnings ?? []).map((w) => (
                            <span key={w} className="mt-0.5 flex items-center gap-1 text-[10px] font-normal text-amber-700">
                              <AlertTriangle className="w-3 h-3 shrink-0" aria-hidden="true" />
                              {w}
                            </span>
                          ))}
                        </td>
                        <td className="px-3 py-2 text-[#717182]">
                          {initiative.category}
                        </td>
                        <td className="px-3 py-2 text-[#717182] hidden md:table-cell">
                          {initiative.source}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-[#18365E]">
                          {initiative.percentComplete == null ? <span className="text-[#717182]">–</span> : `${initiative.percentComplete}%`}
                        </td>
                        <td className="px-3 py-2">
                          {initiative.committed ? (
                            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-[#18365E] text-white">Committed</span>
                          ) : (
                            <span className="text-[#717182]">No</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full ${
                            initiative.status === 'Matched'
                              ? 'bg-emerald-100 text-emerald-700'
                              : initiative.status === 'Duplicate'
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-blue-100 text-blue-700'
                          }`}>
                            {initiative.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            )}

            {/* CTA Row */}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setShowFormatModal(true)}
                className="text-xs font-medium text-[#18365E] hover:text-[#FF6E4C] transition-colors flex items-center gap-1"
              >
                <Info className="w-3 h-3" />
                View sample format
              </button>

              {SHOW_COMING_SOON && (
                <button
                  onClick={handleIngestionClick}
                  disabled
                  className="px-4 py-2 text-xs font-semibold text-white bg-[#18365E]/50 rounded-lg cursor-not-allowed"
                >
                  Start Ingestion (Coming Soon)
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Format Modal */}
      {showFormatModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowFormatModal(false)}
        >
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-5"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-sm font-bold text-[#18365E] mb-3">
              Expected CSV/XLSX Format
            </h3>
            <p className="text-xs text-[#717182] mb-3">
              Your file should include the following columns:
            </p>
            <div className="bg-[#F5F7FA] rounded-lg p-3 mb-4">
              <ul className="space-y-1 text-xs text-[#18365E]">
                <li><span className="font-semibold">Initiative Name</span>: Name of the initiative (required)</li>
                <li><span className="font-semibold">Category</span>: Domain or category</li>
                <li><span className="font-semibold">Source</span>: Where this came from</li>
                <li><span className="font-semibold">Percent Complete</span>: 0 to 100; "45" or "45%" (optional)</li>
                <li><span className="font-semibold">Committed</span>: Yes/No: already committed, so it does not need evaluating (default No)</li>
                <li><span className="font-semibold">FTEs Committed</span>: Staff already assigned (optional)</li>
                <li><span className="font-semibold">Notes</span>: Additional context (optional)</li>
              </ul>
            </div>
            <p className="text-[11px] text-[#717182] mb-4 italic">
              Status is not a column. It is derived on import (Matched / Duplicate / New).
              A committed initiative that matches a library use case carries its Committed flag
              into Evaluate. Percent Complete is shown for context and does not change any score.
            </p>
            <div className="flex gap-2 mb-4">
              <a
                href="/templates/D5_Existing_Initiatives_TEMPLATE.xlsx"
                download
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-[#18365E] border border-[#18365E]/30 rounded-lg hover:bg-[#F5F7FA]"
              >
                <Download className="w-3 h-3" aria-hidden="true" /> Template
              </a>
              <a
                href="/templates/D5_Existing_Initiatives_ACME_EXAMPLE.xlsx"
                download
                className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 text-xs font-medium text-[#18365E] border border-[#18365E]/30 rounded-lg hover:bg-[#F5F7FA]"
              >
                <Download className="w-3 h-3" aria-hidden="true" /> {DEMO_CLIENT_NAME} example
              </a>
            </div>
            <button
              onClick={() => setShowFormatModal(false)}
              className="w-full px-4 py-2 text-xs font-semibold text-white bg-[#18365E] rounded-lg hover:bg-[#18365E]/90 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default InitiativesUploadBand;